import type { Bill, BillVarianceFlag } from '../api/types';
import { Card, Badge } from './ui';

interface Props {
  bill: Bill;
}

function parseJson<T>(s: string | null): T | null {
  if (!s) return null;
  try { return JSON.parse(s) as T; } catch { return null; }
}

const fmtNum = (n: unknown, digits = 2) =>
  typeof n === 'number'
    ? n.toLocaleString('en-ZA', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : String(n);

// Full multi-line flag detail blocks (type, sub-details, computed values),
// not short enum labels — doesn't fit Badge's inline-chip shape, so this
// stays hand-rolled. dark: added: bg-{color}-900/text-{color}-100 clear the
// >1.2 "distinct from card" bar against surfaceRaised(dark) (red 1.51,
// amber 1.67, blue 1.46) and their own text/bg pairs clear AA (red-100 on
// red-900 = 8.20:1, amber-100 on amber-900 = 8.15:1, blue-100 on blue-900 =
// 8.49:1) — red/amber-950 or blue-950 were tried first and rejected: they're
// only ~1.0-1.06:1 against surfaceRaised(dark), i.e. functionally invisible
// against the Card these flags render inside (the same collision class
// Badge's own 'neutral' tone shipped with, see ui/Badge.tsx).
const severityClass = (s: BillVarianceFlag['severity']) =>
  s === 'error'   ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-800 dark:text-red-100'
  : s === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900 dark:border-amber-800 dark:text-amber-100'
  : 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-100';

export default function BillAuditPanel({ bill }: Props) {
  const registerTotals = parseJson<Record<string, unknown>>(bill.register_totals);
  const intervalTotals = parseJson<Record<string, unknown>>(bill.interval_totals);
  const flags = parseJson<BillVarianceFlag[]>(bill.variance_flags) || [];

  return (
    <Card className="shadow-sm mb-6">
      <div className="px-6 py-4 border-b border-border-token flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-content">Audit Trail</h2>
          <p className="text-xs text-content-muted mt-0.5">
            Frozen at bill creation. Register snapshots are what the council sees; interval-derived totals are our cross-check.
          </p>
        </div>
        {flags.length > 0 && (
          <Badge tone="warning">{flags.length} variance flag{flags.length === 1 ? '' : 's'}</Badge>
        )}
      </div>

      <div className="p-6 space-y-6">
        {flags.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-content mb-2">Variance flags</h3>
            <div className="space-y-2">
              {flags.map((f, idx) => (
                <div key={idx} className={`border rounded-md p-3 text-sm ${severityClass(f.severity)}`}>
                  <div className="font-medium">
                    {f.type.replace(/_/g, ' ')}
                    {f.bucket && <span className="ml-2 text-xs opacity-70">({f.bucket})</span>}
                    {f.register && <span className="ml-2 font-mono text-xs opacity-70">[{f.register}]</span>}
                  </div>
                  {f.detail && <div className="text-xs mt-1 opacity-80">{f.detail}</div>}
                  {(f.register_value !== undefined || f.computed_value !== undefined) && (
                    <div className="text-xs mt-1 font-mono">
                      register: {fmtNum(f.register_value, 4)} · computed: {fmtNum(f.computed_value, 4)}
                      {f.variance_pct !== undefined && (
                        <> · Δ {fmtNum(f.variance_pct, 3)}%
                          {f.tolerance_pct !== undefined && ` (tolerance ${f.tolerance_pct}%)`}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {registerTotals && (
            <section>
              <h3 className="text-sm font-semibold text-content mb-2">Register snapshots</h3>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-content-muted">
                  <tr>
                    <th className="text-left py-1">Register</th>
                    <th className="text-right py-1">Opening</th>
                    <th className="text-right py-1">Closing</th>
                    <th className="text-right py-1">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(registerTotals).map(([reg, val]) => {
                    const v = val as { opening?: number; closing?: number; delta?: number; value?: number; timestamp?: string };
                    if (v.delta !== undefined) {
                      return (
                        <tr key={reg} className="border-t border-border-token">
                          <td className="py-1 font-mono">{reg}</td>
                          <td className="text-right py-1 font-mono">{fmtNum(v.opening)}</td>
                          <td className="text-right py-1 font-mono">{fmtNum(v.closing)}</td>
                          <td className="text-right py-1 font-mono font-semibold">{fmtNum(v.delta)}</td>
                        </tr>
                      );
                    }
                    // MD register: single value + timestamp
                    return (
                      <tr key={reg} className="border-t border-border-token">
                        <td className="py-1 font-mono">{reg}</td>
                        <td className="text-right py-1 font-mono" colSpan={3}>
                          {fmtNum(v.value)} {v.timestamp && <span className="text-content-muted">@ {new Date(v.timestamp).toLocaleString('en-ZA')}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </section>
          )}

          {intervalTotals && (
            <section>
              <h3 className="text-sm font-semibold text-content mb-2">Interval-derived totals</h3>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(intervalTotals).map(([k, v]) => (
                    <tr key={k} className="border-t border-border-token">
                      <td className="py-1 text-content-muted">{k.replace(/_/g, ' ')}</td>
                      <td className="text-right py-1 font-mono">{
                        typeof v === 'number' ? fmtNum(v, 4)
                        : v === null || v === undefined ? '—'
                        : String(v)
                      }</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </section>
          )}
        </div>
      </div>
    </Card>
  );
}
